import {
  AccountLimitedResponse,
  ParentLimitedResponse,
  StudentLimitedResponse,
  TaskRecipientCreate,
  TaskRecipientResponse,
  TutorPublicResponse,
} from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import { Select, selectComponents } from "components/Select";
import { useState } from "react";
import { RECENT_LABEL, recentAmong, useRecentPeople } from "util/recentPeople";
import {
  components,
  MenuListProps,
  MultiValueGenericProps,
} from "react-select";
import "./PeoplePicker.css";

export type TaskRole = "admin" | PersonKind;

/** Someone a task can be assigned to or be about. */
export interface TaskPerson {
  role: TaskRole;
  /** An admin's account reference id, or a tutor, parent or student id. */
  id: string | number;
  label: string;
}

/** The roles in the order tasks have always listed them. */
export const ROLES: { role: TaskRole; one: string; many: string }[] = [
  { role: "admin", one: "Admin", many: "Admins" },
  { role: PersonKind.TUTOR, one: "Tutor", many: "Tutors" },
  { role: PersonKind.PARENT, one: "Parent", many: "Parents" },
  { role: PersonKind.STUDENT, one: "Student", many: "Students" },
];

/**
 * "First Last", without the stray spaces and tabs some saved names carry
 * (a name starting with a tab sorted ahead of A, and a trailing space made
 * two spaces that a search for the full name missed).
 */
export const fullName = (person: {
  first_name?: string | null;
  last_name?: string | null;
}) => `${person.first_name} ${person.last_name}`.replace(/\s+/g, " ").trim();

/** By name, A to Z. */
export const byName = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label);

/** The organization's people as one list for the picker. */
export const taskPeople = ({
  admins,
  tutors,
  parents,
  students,
}: {
  admins?: AccountLimitedResponse[];
  tutors?: TutorPublicResponse[];
  parents?: ParentLimitedResponse[];
  students?: StudentLimitedResponse[];
}): TaskPerson[] => [
  ...(admins ?? []).map((admin) => ({
    role: "admin" as const,
    id: admin.reference_id,
    label: fullName(admin),
  })),
  ...(tutors ?? []).map((tutor) => ({
    role: PersonKind.TUTOR,
    id: tutor.id,
    label: fullName(tutor),
  })),
  ...(parents ?? []).map((parent) => ({
    role: PersonKind.PARENT,
    id: parent.id,
    label: fullName(parent),
  })),
  ...(students ?? []).map((student) => ({
    role: PersonKind.STUDENT,
    id: student.id,
    label: fullName(student),
  })),
];

/**
 * The chosen people as the API's recipients, one id each, in the order
 * tasks have always sent them (admins, tutors, parents, students).
 */
export const toRecipients = (chosen: TaskPerson[]): TaskRecipientCreate[] =>
  ROLES.flatMap(({ role }) =>
    chosen.filter((person) => person.role === role)
  ).map((person) => ({
    account_id: person.role === "admin" ? String(person.id) : null,
    student_id: person.role === PersonKind.STUDENT ? Number(person.id) : null,
    parent_id: person.role === PersonKind.PARENT ? Number(person.id) : null,
    tutor_id: person.role === PersonKind.TUTOR ? Number(person.id) : null,
  }));

const personKey = (person: TaskPerson) => `${person.role}:${person.id}`;

/** Someone on a task, as the people picker shows them. */
export const recipientPerson = (user: TaskRecipientResponse): TaskPerson[] => {
  if (user.account) {
    return [
      {
        role: "admin",
        id: user.account.reference_id,
        label: fullName(user.account),
      },
    ];
  }
  if (user.tutor) {
    return [
      {
        role: PersonKind.TUTOR,
        id: user.tutor.id,
        label: fullName(user.tutor),
      },
    ];
  }
  if (user.parent) {
    return [
      {
        role: PersonKind.PARENT,
        id: user.parent.id,
        label: fullName(user.parent),
      },
    ];
  }
  if (user.student) {
    return [
      {
        role: PersonKind.STUDENT,
        id: user.student.id,
        label: fullName(user.student),
      },
    ];
  }
  return [];
};

/** Who to add and who to remove, as the API's recipients, from one list to another. */
export const recipientChanges = (
  original: TaskPerson[],
  updated: TaskPerson[]
) => {
  const keys = (people: TaskPerson[]) => new Set(people.map(personKey));
  const before = keys(original);
  const after = keys(updated);
  return {
    add: toRecipients(
      updated.filter((person) => !before.has(personKey(person)))
    ),
    remove: toRecipients(
      original.filter((person) => !after.has(personKey(person)))
    ),
  };
};

/** Listed before anything is typed, and the most matches listed. */
const SUGGESTED = 8;
const MATCHES = 20;

/**
 * A menu fitted to the window (fitMenuToWindow): the most it shows before
 * it scrolls, the least it is cut to, and the room it leaves around itself
 * (its gap from the field, its border and padding, and the window's edge),
 * in pixels.
 */
const FITTED_MENU_MOST = 260;
const FITTED_MENU_LEAST = 120;
const FITTED_MENU_ROOM = 24;

type MenuFit = { placement: "top" | "bottom"; maxHeight: number };

/**
 * Where a menu fitted to the window opens, from its field's place in the
 * window: below when it fits there or there is more room below, else
 * above, cut to the room it has.
 */
function fitMenu(inputId: string): MenuFit | undefined {
  const control = document
    .getElementById(inputId)
    ?.closest(".task-field__control");
  if (!control) return undefined;
  const { top, bottom } = control.getBoundingClientRect();
  const below = window.innerHeight - bottom - FITTED_MENU_ROOM;
  const above = top - FITTED_MENU_ROOM;
  const placement =
    below >= FITTED_MENU_MOST || below >= above ? "bottom" : "top";
  const room = placement === "bottom" ? below : above;
  return {
    placement,
    maxHeight: Math.max(Math.min(FITTED_MENU_MOST, room), FITTED_MENU_LEAST),
  };
}

/** A chip: the name, then the role in its color. */
const PersonChip = (props: MultiValueGenericProps<TaskPerson, true>) => (
  <components.MultiValueLabel {...props}>
    {props.children}
    <span className={`task-person-role task-person-role--${props.data.role}`}>
      {ROLES.find(({ role }) => role === props.data.role)?.one}
    </span>
  </components.MultiValueLabel>
);

/** The menu, with a muted line under the people when the list is cut short. */
const MenuWithNote = (props: MenuListProps<TaskPerson, true>) => {
  const { menuNote } = props.selectProps as { menuNote?: string };
  return (
    <components.MenuList {...props}>
      {props.children}
      {menuNote && <div className="task-picker-note">{menuNote}</div>}
    </components.MenuList>
  );
};

/** People as the menu's groups, one per role (empty ones left out). */
const byRole = (people: TaskPerson[]) =>
  ROLES.map(({ role, many }) => ({
    label: many,
    options: people.filter((person) => person.role === role),
  })).filter((group) => group.options.length > 0);

/**
 * One search across everyone who can be picked (the roles allowed, from
 * `people`, see taskPeople). Before anything is typed it lists a few people
 * as one Recent list: those in context first, then the people the
 * signed-in admin dealt with lately. Before there are any recent people, it
 * lists those in context and then everyone by name, grouped by role.
 * Typing searches names, and the matches are grouped by role. Chosen people
 * show as chips with their role and leave the menu.
 */
export default function PeoplePicker({
  inputId,
  people,
  roles = ROLES.map(({ role }) => role),
  suggested = [],
  value,
  onChange,
  placeholder,
  autoFocus = false,
  fitMenuToWindow = false,
}: {
  /** For the field's label. */
  inputId: string;
  people: TaskPerson[];
  roles?: TaskRole[];
  /** People in context (the signed-in admin, a person and their family). */
  suggested?: TaskPerson[];
  value: TaskPerson[];
  onChange: (value: TaskPerson[]) => void;
  placeholder: string;
  /** Take the cursor and open the list when shown. */
  autoFocus?: boolean;
  /**
   * In a pop-out fixed to the window, with no scrolling box around it: the
   * menu opens below or above, wherever it fits in the window (fitMenu).
   */
  fitMenuToWindow?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [menuFit, setMenuFit] = useState<MenuFit>();
  const recent = useRecentPeople(roles);

  const chosen = new Set(value.map(personKey));
  const allowed = people.filter(
    (person) => roles.includes(person.role) && !chosen.has(personKey(person))
  );
  const search = query.trim().toLowerCase();
  let groups: { label: string; options: TaskPerson[] }[];
  let menuNote: string | undefined;
  if (search) {
    const matches = allowed
      .filter((person) => person.label.toLowerCase().includes(search))
      .sort(byName);
    groups = byRole(matches.slice(0, MATCHES));
    if (matches.length > MATCHES) menuNote = "Keep typing to narrow";
  } else {
    // Those in context in the order given, then the recent people, or
    // everyone else by name before there are any (one in context counts).
    // Only people who can be picked here are suggested.
    const first = suggested.map(personKey);
    const rank = (person: TaskPerson) => {
      const at = first.indexOf(personKey(person));
      return at < 0 ? first.length : at;
    };
    const inContext = allowed
      .filter((person) => first.includes(personKey(person)))
      .sort((a, b) => rank(a) - rank(b));
    const recentListed = recentAmong(
      recent,
      allowed.filter((person) => !first.includes(personKey(person))),
      personKey
    );
    let listed: TaskPerson[];
    if (recentAmong(recent, allowed, personKey).length > 0) {
      listed = [...inContext, ...recentListed].slice(0, SUGGESTED);
      groups = [{ label: RECENT_LABEL, options: listed }];
    } else {
      listed = [...allowed]
        .sort((a, b) => rank(a) - rank(b) || byName(a, b))
        .slice(0, SUGGESTED);
      groups = byRole(listed);
    }
    if (allowed.length > listed.length) menuNote = "Type to search everyone";
  }

  return (
    <Select
      inputId={inputId}
      // Sized with the form's other fields (create.css).
      classNamePrefix="task-field"
      isMulti
      isClearable={false}
      hideSelectedOptions
      components={{
        ...selectComponents,
        MultiValueLabel: PersonChip,
        MenuList: MenuWithNote,
      }}
      options={groups}
      // The list above is already searched and cut short.
      filterOption={null}
      onInputChange={(text: string) => setQuery(text)}
      menuNote={menuNote}
      // Opens upward near the bottom of a dialog; in a pop-out, where
      // fitMenu says.
      menuPlacement={menuFit?.placement ?? "auto"}
      maxMenuHeight={menuFit?.maxHeight}
      onMenuOpen={
        fitMenuToWindow ? () => setMenuFit(fitMenu(inputId)) : undefined
      }
      value={value}
      onChange={(next: readonly TaskPerson[]) => onChange([...next])}
      getOptionValue={personKey}
      getOptionLabel={(person: TaskPerson) => person.label}
      placeholder={placeholder}
      autoFocus={autoFocus}
      openMenuOnFocus={autoFocus}
      noOptionsMessage={() => "No one matches"}
    />
  );
}
